import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';

const EVENT_ICONS = {
  MARKET_CRASH: { icon: 'TrendingDown', color: 'text-red-400', bg: 'bg-red-500/10' },
  BULL_RUN: { icon: 'TrendingUp', color: 'text-green-400', bg: 'bg-green-500/10' },
  FACTION_TOURNAMENT: { icon: 'Swords', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  STOCK_MULTIPLIER_EVENT: { icon: 'Zap', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  LIQUIDITY_CRISIS: { icon: 'AlertTriangle', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  DIVIDEND_BONANZA: { icon: 'Gift', color: 'text-blue-400', bg: 'bg-blue-500/10' },
};

const TIER_THRESHOLDS = [
  { tier: 'BRONZE', min: 0, max: 1000, color: '#ea580c' },
  { tier: 'SILVER', min: 1000, max: 5000, color: '#94a3b8' },
  { tier: 'GOLD', min: 5000, max: 15000, color: '#eab308' },
  { tier: 'PLATINUM', min: 15000, max: 40000, color: '#06b6d4' },
  { tier: 'DIAMOND', min: 40000, max: 100000, color: '#3b82f6' },
];

const useCountdown = (endDate) => {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setTimeLeft('Ended'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${d}d ${h}h ${m}m`);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [endDate]);
  return timeLeft;
};

const TierUpBanner = ({ tierUpData, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (tierUpData) {
      setTimeout(() => setVisible(true), 50);
      const t = setTimeout(() => {
        setVisible(false);
        setTimeout(onDismiss, 400);
      }, 4500);
      return () => clearTimeout(t);
    }
  }, [tierUpData]);

  if (!tierUpData) return null;

  const newTier = tierUpData?.progress?.current_tier;
  const tierColors = {
    SILVER: { bg: 'from-slate-700/80 to-slate-600/80', border: 'border-slate-400/50', text: 'text-slate-200', glow: 'rgba(148,163,184,0.3)' },
    GOLD: { bg: 'from-yellow-900/80 to-yellow-800/80', border: 'border-yellow-500/50', text: 'text-yellow-300', glow: 'rgba(234,179,8,0.4)' },
    PLATINUM: { bg: 'from-cyan-900/80 to-cyan-800/80', border: 'border-cyan-500/50', text: 'text-cyan-300', glow: 'rgba(6,182,212,0.4)' },
    DIAMOND: { bg: 'from-blue-900/80 to-blue-800/80', border: 'border-blue-500/50', text: 'text-blue-300', glow: 'rgba(59,130,246,0.5)' },
    BRONZE: { bg: 'from-orange-900/80 to-orange-800/80', border: 'border-orange-500/50', text: 'text-orange-300', glow: 'rgba(234,88,12,0.3)' },
  };
  const tc = tierColors?.[newTier] || tierColors?.BRONZE;

  return (
    <div
      className={`relative overflow-hidden rounded-lg border p-4 bg-gradient-to-r ${tc?.bg} ${tc?.border} mb-4`}
      style={{
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease',
        boxShadow: `0 0 30px ${tc?.glow}`,
      }}
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10 border border-white/20">
          <Icon name="Trophy" size={22} className={tc?.text} />
        </div>
        <div>
          <p className="text-xs text-white/60 uppercase tracking-wider font-medium">Tier Up!</p>
          <p className={`font-bold text-lg ${tc?.text}`}>{newTier} Tier Reached</p>
          <p className="text-xs text-white/50">
            {tierUpData?.progress?.season_xp?.toLocaleString()} total season XP
          </p>
        </div>
        <button
          onClick={() => { setVisible(false); setTimeout(onDismiss, 300); }}
          className="ml-auto w-6 h-6 rounded flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
        >
          <Icon name="X" size={12} className="text-white" />
        </button>
      </div>
      {/* Shimmer effect */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%)',
          animation: 'shimmer 2s infinite',
        }}
      />
    </div>
  );
};

const SeasonXPBar = ({ seasonProgress, season }) => {
  const [displayXP, setDisplayXP] = useState(0);
  const animFrameRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)')?.matches;

  const currentXP = seasonProgress?.season_xp || 0;
  const currentTier = seasonProgress?.current_tier || 'BRONZE';
  const tierData = TIER_THRESHOLDS?.find(t => t?.tier === currentTier) || TIER_THRESHOLDS?.[0];
  const nextTierData = TIER_THRESHOLDS?.[TIER_THRESHOLDS?.indexOf(tierData) + 1];

  const tierMin = tierData?.min || 0;
  const tierMax = nextTierData?.min || tierData?.max || 1000;
  const tierProgress = Math.min(((currentXP - tierMin) / (tierMax - tierMin)) * 100, 100);

  useEffect(() => {
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);

    if (isMobile) {
      setDisplayXP(tierProgress);
      return;
    }

    const start = displayXP;
    const end = tierProgress;
    const duration = 800;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayXP(start + (end - start) * eased);
      if (t < 1) animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current); };
  }, [tierProgress]);

  return (
    <div className="mt-3 space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground flex items-center gap-1">
          <span style={{ color: tierData?.color }}>●</span>
          {currentTier}
          {nextTierData && <span className="text-muted-foreground/60"> → {nextTierData?.tier}</span>}
        </span>
        <span className="font-mono text-primary">
          {currentXP?.toLocaleString()} XP
          {nextTierData && (
            <span className="text-muted-foreground"> / {nextTierData?.min?.toLocaleString()}</span>
          )}
        </span>
      </div>
      <div className="h-2.5 bg-muted rounded-full overflow-hidden relative">
        <div
          className="h-full rounded-full relative overflow-hidden"
          style={{
            width: `${displayXP}%`,
            background: `linear-gradient(90deg, ${tierData?.color}cc, ${tierData?.color})`,
            transition: isMobile ? 'width 0.5s ease' : 'none',
            willChange: 'width',
          }}
        >
          {/* Shimmer on XP bar */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
              animation: 'shimmer 2s infinite',
            }}
          />
        </div>
      </div>
      {nextTierData && (
        <p className="text-xs text-muted-foreground text-right">
          {(nextTierData?.min - currentXP)?.toLocaleString()} XP to {nextTierData?.tier}
        </p>
      )}
    </div>
  );
};

const SeasonPanel = ({ season, events, leaderboard, seasonProgress, tierUpData, rankChange, onDismissTierUp }) => {
  const countdown = useCountdown(season?.end_date);

  const seasonProgress_pct = season
    ? Math.min(
        ((new Date() - new Date(season.start_date)) /
          (new Date(season.end_date) - new Date(season.start_date))) *
          100,
        100
      )
    : 0;

  if (!season) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-center">
        <Icon name="Calendar" size={32} className="text-muted-foreground mx-auto mb-2" />
        <p className="text-muted-foreground text-sm">No active season</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tier Up Banner */}
      {tierUpData && (
        <TierUpBanner tierUpData={tierUpData} onDismiss={onDismissTierUp || (() => {})} />
      )}

      {/* Season Header */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={16} className="text-purple-400" />
              <span className="text-xs text-purple-400 font-medium uppercase tracking-wider">
                Season {season?.season_number}
              </span>
              {/* Live indicator */}
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-green-400">Live</span>
              </span>
            </div>
            <h3 className="font-bold text-foreground mt-0.5">{season?.season_theme}</h3>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Ends in</p>
            <p className="text-sm font-mono font-bold text-primary">{countdown}</p>
          </div>
        </div>

        {/* Season timeline progress */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Season Progress</span>
            <span>{Math.round(seasonProgress_pct)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-1000"
              style={{ width: `${seasonProgress_pct}%` }}
            />
          </div>
        </div>

        {/* Real-time XP Bar */}
        {seasonProgress && (
          <SeasonXPBar seasonProgress={seasonProgress} season={season} />
        )}
      </div>

      {/* Active Events */}
      {events?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Active Events
          </h4>
          <div className="space-y-2">
            {events?.map((event) => {
              const style = EVENT_ICONS?.[event?.event_type] || { icon: 'Star', color: 'text-primary', bg: 'bg-primary/10' };
              return (
                <div
                  key={event?.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border border-border/50 ${style?.bg}`}
                >
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${style?.bg}`}>
                    <Icon name={style?.icon} size={14} className={style?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{event?.event_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {event?.multiplier_value !== 1
                        ? `${event?.multiplier_value}x multiplier`
                        : event?.description}
                    </p>
                  </div>
                  {event?.is_active && (
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Season Leaderboard Preview */}
      {leaderboard?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Season Leaderboard
          </h4>
          <div className="space-y-1">
            {leaderboard?.slice(0, 5)?.map((entry, idx) => {
              const isCurrentUser = rankChange && entry?.user_id === entry?.user_id;
              return (
                <div
                  key={entry?.id}
                  className={`flex items-center gap-3 p-2 rounded-lg border transition-all duration-300 ${
                    isCurrentUser && rankChange
                      ? 'bg-primary/5 border-primary/20' :'bg-card/50 border-border/30'
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                      idx === 0
                        ? 'bg-yellow-500 text-black'
                        : idx === 1
                        ? 'bg-slate-400 text-black'
                        : idx === 2
                        ? 'bg-orange-600 text-white' :'bg-muted text-muted-foreground'
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-xs text-foreground truncate">
                    {entry?.user_profiles?.username || 'Player'}
                  </span>
                  {/* Rank change indicator */}
                  {isCurrentUser && rankChange?.diff !== 0 && (
                    <span className={`text-xs font-bold flex items-center gap-0.5 ${
                      rankChange?.diff > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {rankChange?.diff > 0 ? '↑' : '↓'}
                      {Math.abs(rankChange?.diff)}
                    </span>
                  )}
                  <span className="text-xs text-green-400 font-mono">
                    ${entry?.final_net_worth?.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonPanel;
